import React, { useEffect, useRef, useState } from 'react';
import { FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { Grid, Segment, Divider, Message } from 'semantic';
import { getToken } from 'utils/api';

import {
  BillingAndRequestCardFormValues,
  BillingInfo,
} from 'components/BillingInfo/types';

import {
  SignupFormData,
  useCreateAccount,
  useInitialSignupState,
  useSubmitCardRequest,
  useValidateBillingInfo,
  useValidatePersonalDetails,
} from 'screens/Auth/SignupV2/api';
import { useSignupErrorFormatter } from 'screens/Auth/SignupV2/errors';

import {
  AccountVerifiedStep,
  Journey,
  PersonalDetailsStep,
  SignupStep,
  EmailVerified,
  useSignupJourney,
  VerifyEmailStep,
  VerifyPhoneStep,
  BillingInfoAndOrderCardStep,
  PersonalDetailsSSOStep,
  FieldServiceStep,
} from 'screens/Auth/SignupV2/journey';

import { useProvider } from 'screens/Auth/SignupV2/useProvider';
import PersonalDetailsForm, {
  PersonalAndAccountDetails,
} from 'screens/Auth/SignupV2/steps/PersonalDetails';

import FieldServiceForm from './steps/FieldServiceDetails';

import IncompletePersonalDetailsForm from 'screens/Auth/SignupV2/steps/IncompletePersonalDetails';

import { VerifyMobileForm } from 'components/UserVerificationWidget/VerifyMobile';
import { VerifyEmailForm } from 'components/UserVerificationWidget/VerifyEmail';

import Panel from 'screens/Auth/Panel/Panel';
import BillingInfoAndOrderCardForm from 'screens/Auth/SignupV2/steps/BillingInfoAndOrderCard';

import { useProductInstrumentation } from 'contexts/productInstrumentation';
import ProgressSteps from 'components/ProgressSteps';
import { AccountDetails } from 'screens/Auth/SignupV2/steps/AccountDetails';
import { useSubmitBillingInfoMutation } from 'components/BillingInfo/api';
import { useUser } from 'contexts/user';

import LayoutContainer from '../LayoutContainer';
import LoginPanel from 'screens/Auth/SignupV2/LoginPanel';
import RedirectScreen, {
  Variation,
} from 'screens/Auth/SignupV2/steps/Completed/RedirectScreen';

import { identifyUser, trackCustomBehavioralEvent } from 'utils/hubspot';

import { INTERCOM_APP_ID } from 'utils/env';
import { useCanUseAutoPay } from 'hooks/usePaymentMethodOptions';
import sleep from 'utils/sleep';
import { useHistory } from 'react-router';
import request from 'utils/api/request';
import { AccountProvider, useAccount } from 'contexts/account';
import { useEnabledPaymentMethods } from 'contexts/enabledPaymentMethods';
import {
  PaymentMethodsWidgetCallbacks,
  tryStoringPaymentInstrument,
} from 'components/PaymentMethodsWidget/PaymentMethodsWidget';
import { useToast } from 'components/Toast';
import { isAccountAdmin } from 'utils/roles';

export function billingInfoByAccountType(
  billingInfo: BillingInfo,
  accountType: AccountDetails['accountType']
): BillingInfo {
  const baseInfo = {
    paymentMethod: billingInfo.paymentMethod,
    accountHolderName: billingInfo.accountHolderName,
    ibanNumber: billingInfo.ibanNumber,
    bicNumber: billingInfo.bicNumber,
    countryCode: billingInfo.countryCode,
    street: billingInfo.street,
    number: billingInfo.number,
    postalCode: billingInfo.postalCode,
    city: billingInfo.city,
    sepaAccepted: billingInfo.sepaAccepted,
  };
  if (accountType === 'organization') {
    return {
      ...baseInfo,
      vatNumber: billingInfo.vatNumber || '',
      chamberOfCommerceNumber: billingInfo.chamberOfCommerceNumber || '',
      vatNotApplicable: billingInfo.vatNotApplicable || false,
    };
  }
  return baseInfo;
}

export default function SignupV2WithQueryProvider() {
  const { user, fetchUser } = useUser();
  const hasToken = !!getToken();

  if (!user && hasToken) {
    fetchUser();
    return null;
  }

  return (
    <LayoutContainer>
      <AccountProvider>
        <SignupV2 />
      </AccountProvider>
    </LayoutContainer>
  );
}

function getRedirectScreenVariation(
  journey?: Journey,
  submittedCardRequest?: boolean
): Variation {
  if (journey === 'msp') {
    if (submittedCardRequest) {
      return 'charge-card-ordered';
    }
    return 'account-created';
  }
  return 'account-verified';
}

function getTitlesForStep(step: SignupStep, journey: Journey, t: any) {
  if (step === PersonalDetailsStep) {
    if (journey === 'cpo') {
      return {
        title: t('signup.createFreeAccountHeader', 'Create your Account'),
        subTitle: t(
          'signup.createFreeAccountMessage',
          'Start your EV charge journey today'
        ),
      };
    }
    if (journey === 'msp') {
      return {
        title: t('signup.orderChargeCardHeader', 'Order your Charge Card'),
        subTitle: t(
          'signup.billingDetailsMessage',
          `2. Fill in your billing details`
        ),
      };
    }
    if (journey === 'fst') {
      return {
        title: t(
          'signup.fstPersonalDetailsTitle',
          'Create your Service Account'
        ),
        subTitle: t(
          'signup.fstPersonalDetailsSubtitle',
          'Enter your details below to get started'
        ),
      };
    }

    return {};
  }
}

function SignupV2() {
  const { t } = useTranslation();
  const history = useHistory();
  const provider = useProvider();
  const { account, getAccount } = useAccount();
  const { setToken, fetchUser, user } = useUser();

  const dataLayer = ((window as any)?.dataLayer || []) as any[];

  const errorFormatter = useSignupErrorFormatter();
  const canUseAutoPay = useCanUseAutoPay();

  const signupJourney = useSignupJourney();
  const [step, setStep] = useState(signupJourney.initialStep);

  const toast = useToast();

  const { journey } = signupJourney?.userJourney || {};

  const [navigationDirection, setNavigationDirection] = useState<
    'forward' | 'backward' | 'none'
  >('none');

  const { isPayBridgeEnabled, enabledPaymentMethods } =
    useEnabledPaymentMethods();

  const navigateForward = () => {
    const nextStep = signupJourney.nextStep(step as SignupStep);
    if (nextStep) {
      setNavigationDirection('forward');
      setStep(nextStep);
    }
  };

  useEffect(() => {
    if (window?.Intercom && provider && provider.slug === 'e-flux') {
      window.Intercom('boot', {
        app_id: INTERCOM_APP_ID,
      });
    }
  }, [provider]);

  useEffect(() => {
    if (user?.contact?.phoneCountryCode === 'GB' && step === VerifyPhoneStep) {
      // If the user has a UK phone number, we skip the phone verification step
      // Once we send sms we can remove the step
      console.log('Skipping VerifyPhoneStep for UK users');
      navigateForward();
    }
  }, [user, step]);

  const [personalAndAccountDetails, setPersonalAndAccountDetails] =
    useState<PersonalAndAccountDetails>({
      // Personal details
      firstName: '',
      lastName: '',
      email: ``,
      password: '',
      confirmPassword: '',
      countryCode: '',
      phoneCountryCode: '',
      phoneNo: '',
      acceptedPrivacy: false,
      acceptedTerms: false,
      // Account Details
      accountType: 'individual',
    });

  const [billingInfo, setBillingInfo] = useState<BillingInfo>({
    paymentMethod: 'autopay',
    accountHolderName: undefined,
    ibanNumber: undefined,
    bicNumber: undefined,
    countryCode: '',
    street: '',
    number: '',
    postalCode: '',
    city: '',
    sepaAccepted: false,
  });

  const [billingAndRequestCardFormValues, setBillingAndRequestCardFormValues] =
    useState<BillingAndRequestCardFormValues>({
      billing: billingInfo,
      cardHolder: {
        type: 'existing',
        userId: '',
      },
      card: {
        type: 'card',
        billingPlanId: '',
      },
      delivery: {
        useSameAsBilling: true,
      },
      accountDetails: {
        accountType: 'individual',
      },
    });

  const [submittedCardRequest, setSubmittedCardRequest] = useState(false);
  // synchronize billingAndRequestCardFormValues with billingInfo
  useEffect(() => {
    setBillingAndRequestCardFormValues((values) => ({
      ...values,
      billing: billingInfo,
    }));
  }, [billingInfo]);

  // load initial state
  const initialState = useInitialSignupState();

  useEffect(() => {
    if (initialState.data) {
      setPersonalAndAccountDetails({
        ...personalAndAccountDetails,
        countryCode:
          initialState.data?.formState?.countryCode ||
          personalAndAccountDetails.countryCode,
        phoneCountryCode:
          initialState.data?.formState?.countryCode?.toUpperCase?.() ||
          personalAndAccountDetails.phoneCountryCode,
      });

      setBillingInfo((billingInfo) => ({
        ...billingInfo,
        countryCode:
          initialState.data?.formState?.countryCode ||
          personalAndAccountDetails.countryCode,
      }));
    }
    if (!initialState.isLoading) {
      setNavigationDirection('forward');
    }
  }, [initialState.data, initialState.isLoading]);

  const personalAndAccountDetailsFormRef =
    useRef<FormikProps<PersonalAndAccountDetails> | null>(null);

  // Function to validate personal details and then create account
  const validatePersonalDetailsAndCreateAccount = async (
    values: PersonalAndAccountDetails
  ) => {
    try {
      await validatePersonalDetailsMutation.mutateAsync(values);

      // Create account
      let accountDetails: AccountDetails;
      if (values.accountType === 'organization') {
        accountDetails = {
          accountType: 'organization',
          organizationName: values.organizationName,
        };
      } else {
        accountDetails = {
          accountType: 'individual',
        };
      }

      const createAccountPayload = {
        personalDetails: values,
        accountDetails: accountDetails,
      };

      await createAccountMutation.mutateAsync(createAccountPayload);

      identifyUser({ email: values.email });

      navigateForward();
    } catch (error) {
      console.error('Error during validation or account creation:', error);
    }
  };

  const { productInstrumentation } = useProductInstrumentation();

  const validatePersonalDetailsMutation = useValidatePersonalDetails({
    onSuccess: (next) => {
      productInstrumentation?.trackJourneyStep({
        journey: 'sign-up',
        step: 'account-creation',
        stage: 'completed',
        version: '1.3.0',
        providerSlug: provider?.slug || 'unknown',
      });

      setPersonalAndAccountDetails({ ...personalAndAccountDetails, ...next });
      setBillingInfo({
        ...billingInfo,
        countryCode: next.countryCode,
        paymentMethod: canUseAutoPay(next.countryCode) ? 'autopay' : 'manual',
      });

      // GA
      // legacy event name
      dataLayer.push({
        event: `account_completed`,
      });
      dataLayer.push({
        event: `${journey}_account_completed`,
      });

      // Hubspot
      trackCustomBehavioralEvent(`${journey}_account_completed`, {});
    },
    onError: (error) => {
      productInstrumentation?.trackJourneyStep({
        journey: 'sign-up',
        step: 'account-creation',
        stage: 'errored',
        version: '1.3.0',
        providerSlug: provider?.slug || 'unknown',
      });

      error.details?.forEach((detail) =>
        errorFormatter(detail, personalAndAccountDetailsFormRef.current)
      );
    },
  });

  const createAccountMutation = useCreateAccount<SignupFormData>({
    userJourney: signupJourney.userJourney,
    onSuccess: async (next) => {
      if (next.token) {
        setToken(next.token);
        await fetchUser({
          token: next.token,
        });
        await getAccount?.();
      }
      navigateForward();
    },
    onError: (error) => {
      error.details?.forEach((detail) => {
        errorFormatter(detail, personalAndAccountDetailsFormRef.current);
      });
    },
  });

  const billingInfoFormRef =
    useRef<FormikProps<BillingAndRequestCardFormValues> | null>(null);

  const paymentMethodWidgetRef = useRef<PaymentMethodsWidgetCallbacks>(null);

  const validateAndSubmitBillingInfoMutation = useValidateBillingInfo({
    onSuccess: async (next) => {
      // update account billing information
      await submitBillingInfoMutation.mutateAsync(next);

      productInstrumentation?.trackJourneyStep({
        journey: 'sign-up',
        step: 'billing-details',
        stage: 'completed',
        version: '1.3.0',
        planId: journeyBillingPlanId || 'not-set',
        providerSlug: provider?.slug || 'unknown',
      });
      // GA
      // legacy event name
      dataLayer.push({
        event: `billing_completed`,
        accountBillingCountry: account?.billing?.countryCode?.toUpperCase(),
      });
      // new name
      dataLayer.push({
        event: `${journey}_billing_completed`,
        accountBillingCountry: account?.billing?.countryCode?.toUpperCase(),
      });

      // Hubspot
      trackCustomBehavioralEvent(`${journey}_billing_completed`, {});
    },

    onError: (error) => {
      error.details?.forEach((detail) => {
        errorFormatter(detail, billingInfoFormRef.current);
      });

      productInstrumentation?.trackJourneyStep({
        journey: 'sign-up',
        step: 'billing-details',
        stage: 'errored',
        version: '1.3.0',
        providerSlug: provider?.slug || 'unknown',
      });
    },
  });

  const submitBillingInfoMutation = useSubmitBillingInfoMutation({
    account,
    onError: (error) => {
      error.details?.forEach((detail) => {
        errorFormatter(detail, billingInfoFormRef.current);
      });
    },
  });

  const submitCardRequestMutation = useSubmitCardRequest({
    onSuccess: () => {
      productInstrumentation?.trackJourneyStep({
        journey: 'sign-up',
        stage: 'completed',
        step: 'plan-selection',
        version: '1.3.0',
        providerSlug: provider?.slug || 'unknown',
      });
      setSubmittedCardRequest(true);
      // GA
      dataLayer.push({
        event: `${journey}_plan_completed`,
      });

      // Hubspot
      trackCustomBehavioralEvent(`${journey}_plan_completed`, {});
      navigateForward();
    },
    onError: () => {
      productInstrumentation?.trackJourneyStep({
        journey: 'sign-up',
        stage: 'errored',
        step: 'plan-selection',
        version: '1.3.0',
        providerSlug: provider?.slug || 'unknown',
      });
    },
  });

  const progressSteps = signupJourney.steps
    .map((step, index) => {
      if (step === PersonalDetailsSSOStep) {
        return {
          label: `${index + 1}. ${t(`signup.stepCompleteYourAccount`, 'Complete Your Account')}`,
        };
      }
      if (step === FieldServiceStep) {
        return {
          label: `${index + 1}. ${t(`signup.fieldServiceStep`, 'Service Details')}`,
        };
      }
      if (step === PersonalDetailsStep) {
        return {
          label: `${index + 1}. ${t(`signup.stepCreateAccount`, 'Create Account')}`,
        };
      } else if (step === VerifyPhoneStep) {
        return {
          label: `${index + 1}. ${t(`signup.stepVerifyPhone`, 'Verify Phone')}`,
        };
      } else if (step === VerifyEmailStep) {
        return {
          label: `${index + 1} ${t(`signup.stepVerifyEmail`, 'Verify Email')}`,
        };
      } else if (step === BillingInfoAndOrderCardStep) {
        return {
          label: `${index + 1} ${t(`signup.stepBillingAndOrderCard`, 'Card Type & Billing')}`,
        };
      } else if (step === AccountVerifiedStep || step === EmailVerified) {
        return null;
      }
    })
    .filter(Boolean) as { label: string }[];

  const storePaymentInstrumentAndOrderCard = async () => {
    if (!account || !isPayBridgeEnabled) {
      return;
    }
    try {
      const isPIStored = await tryStoringPaymentInstrument(
        account,
        enabledPaymentMethods
      );

      if (!isPIStored) {
        // The flow is initial or PI token is missing
        return;
      }

      toast.success(
        t(
          'signup.paymentInstrumentSaved',
          'Payment Instrument saved successfully'
        )
      );

      productInstrumentation?.trackJourneyStep({
        journey: 'sign-up',
        step: 'bank-verification',
        stage: 'completed',
        version: '1.3.0',
        providerSlug: provider?.slug || 'unknown',
      });

      dataLayer.push({
        event: `bank_verification_completed`,
      });

      dataLayer.push({
        event: `${journey}_bank_verification_completed`,
      });

      const pendingCardOrder = getPendingCardOrder();
      if (!pendingCardOrder) {
        // This is storing a PI with no card order, which can be done in Generic and CPO flows
        navigateForward();
        return;
      }
      toast.info(t('signup.orderingCard', 'Requesting a card'));

      submitCardRequestMutation.mutate(pendingCardOrder);

      clearPendingCardOrder();
    } catch (e) {
      console.error('Error storing payment instrument:', e);
      toast.error(
        t(
          'errorStoringPaymentInstrument',
          'Error Storing Payment Instrument, please retry'
        )
      );
    }
  };

  // Send the card order if coming back from authorize redirect
  useEffect(() => {
    if (isPayBridgeEnabled && account) {
      storePaymentInstrumentAndOrderCard();
    }
  }, [account, isPayBridgeEnabled]);

  useEffect(() => {
    // NOTE(amir): Alongside other conditions, we check if the user has Account Admin role.
    // This is a workaround to ensure that the regular user does not see the signup flow.
    if ((!step && user) || (user && !isAccountAdmin(user))) {
      request({
        method: 'POST',
        path: '/1/users/me/signup-completed',
      }).then(() => {
        return history.push(signupJourney?.postSignupRedirect || '/');
      });
    }
  }, [step, user]);

  const showProgressSteps =
    progressSteps.length > 1 &&
    ![AccountVerifiedStep, EmailVerified].includes(step as string);

  const showEmailVerifiedMessage =
    step === BillingInfoAndOrderCardStep && user?.emailVerifiedAt;

  const showPhoneVerified =
    step === VerifyEmailStep && user?.contact?.phoneNoVerifiedAt;

  const journeyBillingPlanId =
    user?.signup?.billingPlanId || signupJourney.userJourney.billingPlanId;

  // NOTE(amir): Alongside other conditions, we check if the user has Account Admin role.
  // This is a workaround to ensure that the regular user does not see the signup flow.
  if ((!step && user) || (user && !isAccountAdmin(user))) {
    return null;
  }

  // if enableSelfSignup is false, dont allow user + account creation
  // we scope it to this step, to keep allowing sso signup if that is enabled
  if (!provider.enableSelfSignup && step === PersonalDetailsStep) {
    return (
      <Message info>
        {t('signup.selfSignupDisabled', `Self-signup is not enabled`)}
      </Message>
    );
  }

  const titles = journey && getTitlesForStep(step as SignupStep, journey, t);

  return (
    <Grid centered verticalAlign="middle" style={{ marginBottom: 0 }}>
      <Grid.Column
        style={{
          maxWidth: '1400px', //minWidth: '80%'
        }}
        mobile={16}
        tablet={16}
        computer={step === BillingInfoAndOrderCardStep ? 14 : 10}>
        {showEmailVerifiedMessage && (
          <Message success align="center">
            {t(
              'signup.emailVerifiedSuccess',
              'E-mail address has been verified successfully.'
            )}
          </Message>
        )}
        {showPhoneVerified && (
          <Message success align="center">
            {t(
              'signup.phoneVerifiedSuccess',
              'Your phone number has been verified successfully.'
            )}
          </Message>
        )}

        {showProgressSteps && (
          <Segment style={{ margin: 0 }}>
            <div style={{ margin: '0 auto' }}>
              <ProgressSteps
                steps={progressSteps}
                currentStep={signupJourney.steps.indexOf(step as SignupStep)}
              />
            </div>
          </Segment>
        )}

        <Grid stackable style={{ marginBottom: 0 }}>
          {step === PersonalDetailsSSOStep && (
            <Panel
              navigateDirection={navigationDirection}
              headerText={t(
                'signup.ssoHeader',
                'Get the most out of your charging stations'
              )}
              content={
                <IncompletePersonalDetailsForm
                  onSubmit={async () => {
                    navigateForward();
                  }}
                />
              }
            />
          )}

          {step === FieldServiceStep && (
            <Panel
              navigateDirection={navigationDirection}
              headerText={t(
                'signup.ssoHeader',
                'Get the most out of your charging stations'
              )}
              content={
                <FieldServiceForm
                  onSubmit={async () => {
                    navigateForward();
                  }}
                />
              }
            />
          )}

          {step === PersonalDetailsStep && (
            <Panel
              errorMessage={
                validatePersonalDetailsMutation.error
                  ? t(
                      'signup.personalDetailsValidationError',
                      'There was a problem verifying your personal information'
                    )
                  : undefined
              }
              navigateDirection={navigationDirection}
              headerText={
                titles?.title ||
                t('signup.createFreeAccountHeader', 'Create your Account')
              }
              subHeaderText={
                titles?.subTitle ||
                t(
                  'signup.billingDetailsMessage',
                  `2. Fill in your billing details`
                )
              }
              content={
                <PersonalDetailsForm
                  forceAccountType={
                    journey === 'fst' ? 'organization' : undefined
                  }
                  formikRef={personalAndAccountDetailsFormRef}
                  value={personalAndAccountDetails}
                  onSubmit={async (next) => {
                    setBillingInfo(
                      billingInfoByAccountType(billingInfo, next.accountType)
                    );

                    setPersonalAndAccountDetails(next);
                    return validatePersonalDetailsAndCreateAccount(next);
                  }}
                  showErrorLabels={validatePersonalDetailsMutation.isError}
                />
              }
              belowContent={
                <Grid.Column>
                  <LoginPanel
                    redirectTo={signupJourney.postSignupRedirect}
                    textAlign="center"
                    text={t(
                      'signup.alreadyHaveAnAccount',
                      'Already have an account?'
                    )}
                  />
                </Grid.Column>
              }
            />
          )}
          {step === BillingInfoAndOrderCardStep && (
            <Panel
              step={step}
              isLoading={
                validateAndSubmitBillingInfoMutation.isLoading ||
                createAccountMutation.isLoading
              }
              errorMessage={
                validateAndSubmitBillingInfoMutation.error?.message ||
                submitCardRequestMutation.error?.message
              }
              navigateDirection={navigationDirection}
              headerText={t(
                'signup.orderChargeCardHeader',
                'Order your Charge Card'
              )}
              subHeaderText={t(
                'signup.billingDetailsMessage',
                `Fill in your billing details`
              )}
              content={
                <BillingInfoAndOrderCardForm
                  planId={journeyBillingPlanId}
                  formikRef={billingInfoFormRef}
                  value={billingAndRequestCardFormValues}
                  redirectUrl={window.location.href}
                  authorizePaymentInstrumentRef={paymentMethodWidgetRef}
                  onSubmit={async (next) => {
                    // update account billing information
                    await validateAndSubmitBillingInfoMutation.mutateAsync(
                      next.billing
                    );

                    if (!user) {
                      return;
                    }

                    let delivery = undefined;

                    if (!next.delivery.useSameAsBilling) {
                      delivery = {
                        city: next.delivery.city,
                        firstName: next.delivery.firstName,
                        lastName: next.delivery.lastName,
                        addressLine2: next.delivery.number,
                        postalCode: next.delivery.postalCode,
                        address: next.delivery.street,
                        countryCode: next.delivery.countryCode.toUpperCase(),
                      };
                    }

                    if (isPayBridgeEnabled) {
                      productInstrumentation?.trackJourneyStep({
                        journey: 'sign-up',
                        step: 'bank-verification',
                        stage: 'started',
                        version: '1.3.0',
                        providerSlug: provider?.slug || 'unknown',
                      });

                      // Save the order in localStorage and navigate to iDeal for authorization.
                      savePendingCardOrder({
                        requestUserId: user.id as string,
                        requestUser: false,
                        billingPlanId: next.card.billingPlanId,
                        type: next.card.type,
                        delivery: delivery,
                      });

                      // Order now happen when back from authorization flow
                      paymentMethodWidgetRef.current
                        ?.authorizeActiveOption()
                        .then((response) => {
                          if (!response.success) {
                            errorFormatter(
                              t(
                                'errorStoringPaymentInstrument',
                                'Error Storing Payment Instrument, please retry'
                              ),
                              billingInfoFormRef.current
                            );
                            return;
                          }

                          if (response.success) {
                            if (response.redirectUrl) {
                              window.location.href = response.redirectUrl;
                            } else {
                              // Non-redirect CC instruments
                              navigateForward();
                            }
                          }
                        })
                        .catch((error) => {
                          productInstrumentation?.trackJourneyStep({
                            journey: 'sign-up',
                            step: 'bank-verification',
                            stage: 'errored',
                            version: '1.3.0',
                            providerSlug: provider?.slug || 'unknown',
                          });

                          console.error(
                            'Error authorizing payment instrument:',
                            error
                          );
                          errorFormatter(
                            t(
                              'errorStoringPaymentInstrument',
                              'Error Storing Payment Instrument, please retry'
                            ),
                            billingInfoFormRef.current
                          );
                        });
                    } else {
                      // order charge card
                      submitCardRequestMutation.mutate({
                        requestUserId: user.id as string,
                        requestUser: false,
                        billingPlanId: next.card.billingPlanId,
                        type: next.card.type,
                        delivery: delivery,
                      });
                    }
                  }}
                  showErrorLabels={validateAndSubmitBillingInfoMutation.isError}
                />
              }
            />
          )}
          {step === VerifyPhoneStep && (
            <Panel
              navigateDirection={navigationDirection}
              headerText={t(
                'signup.verifyYourPhoneNumber',
                'Verify your Phone Number'
              )}
              subHeaderText={t(
                'signup.checkYourMobilePhone',
                `Check your mobile phone`
              )}
              content={
                <>
                  <VerifyMobileForm
                    onError={() => {
                      productInstrumentation?.trackJourneyStep({
                        journey: 'sign-up',
                        step: 'phone-verification',
                        stage: 'errored',
                        version: '1.3.0',
                        providerSlug: provider?.slug || 'unknown',
                      });
                    }}
                    onSuccess={async () => {
                      productInstrumentation?.trackJourneyStep({
                        journey: 'sign-up',
                        step: 'phone-verification',
                        stage: 'completed',
                        version: '1.3.0',
                        providerSlug: provider?.slug || 'unknown',
                      });

                      dataLayer.push({
                        event: `phone_verification_completed`,
                      });
                      dataLayer.push({
                        event: `${journey}_phone_verification_completed`,
                      });
                      // Hubspot
                      trackCustomBehavioralEvent(
                        `${journey}_phone_verification_completed`,
                        {}
                      );

                      navigateForward();
                    }}
                  />
                  {provider?.supportEmail && (
                    <>
                      <Divider />
                      <p style={{ fontSize: '12px', marginBottom: '1em' }}>
                        If you need assistance, please contact our support team
                        at{' '}
                        <a
                          target="_blank"
                          href={`mailto:${provider?.supportEmail}`}>
                          {provider?.supportEmail}
                        </a>
                      </p>
                    </>
                  )}
                  {/*
                  <Accordion fluid styled size="small" exclusive>
                    <Accordion.Title
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                      icon={<Icon name="chevron-down" />}
                      open={true}
                      index={0}
                      active={true}
                      content="need help"></Accordion.Title>
                    <Accordion.Content active={true}>content</Accordion.Content>
                    <Accordion.Title
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                      index={0}
                      icon={<Icon name="chevron-right" />}
                      active={false}
                      content="need help"></Accordion.Title>
                    <Accordion.Content>content</Accordion.Content>
                  </Accordion>
                  */}
                </>
              }
            />
          )}
          {step === VerifyEmailStep && (
            <Panel
              navigateDirection={navigationDirection}
              headerText={t('signup.verifyEmail', 'Verify your e-mail address')}
              subHeaderText={t(
                'signup.verifyEmailSubHeader',
                `Open your e-mail`
              )}
              content={
                <>
                  <VerifyEmailForm
                    onError={() => {
                      productInstrumentation?.trackJourneyStep({
                        journey: 'sign-up',
                        step: 'email-verification',
                        stage: 'errored',
                        version: '1.3.0',
                        providerSlug: provider?.slug || 'unknown',
                      });
                    }}
                    onSuccess={async () => {
                      productInstrumentation?.trackJourneyStep({
                        journey: 'sign-up',
                        step: 'email-verification',
                        stage: 'completed',
                        version: '1.3.0',
                        providerSlug: provider?.slug || 'unknown',
                      });

                      dataLayer.push({
                        event: `email_verification_completed`,
                      });
                      dataLayer.push({
                        event: `${journey}_email_verification_completed`,
                      });
                      // Hubspot
                      trackCustomBehavioralEvent(
                        `${journey}_email_verification_completed`,
                        {}
                      );

                      navigateForward();
                    }}
                  />
                  {provider?.supportEmail && (
                    <>
                      <p style={{ fontSize: '12px', marginBottom: '1em' }}>
                        If you need assistance, please contact our support team
                        at{' '}
                        <a
                          target="_blank"
                          href={`mailto:${provider?.supportEmail}`}>
                          {provider?.supportEmail}
                        </a>
                      </p>
                    </>
                  )}
                  <Divider />
                </>
              }
            />
          )}
          {step === AccountVerifiedStep && (
            <RedirectScreen
              isLoading={false}
              onSubmit={async () => {
                await request({
                  method: 'POST',
                  path: `/1/users/me/signup-completed`,
                });
                await fetchUser();

                // this more DX, when you signup again you should get the welcome message
                window.localStorage.removeItem('dismissWelcomeModal');

                // GA
                dataLayer.push({
                  event: `${journey}_signup_completed`,
                });

                // Hubspot
                trackCustomBehavioralEvent(`${journey}_signup_completed`, {});
                await sleep(500);
                history.push(signupJourney.postSignupRedirect || '/dashboard');
              }}
              postSignupRedirect={signupJourney.postSignupRedirect}
              navigationDirection={navigationDirection}
              variation={getRedirectScreenVariation(
                signupJourney.userJourney.journey,
                submittedCardRequest
              )}
              loginPanelText={t(
                'signup.loginManually',
                'Automatic login not working?'
              )}
            />
          )}
        </Grid>
      </Grid.Column>
    </Grid>
  );
}

type SavePendingCardOrderArgs = {
  requestUserId: string;
  requestUser: boolean;
  billingPlanId: string;
  type: 'card' | 'tag';
  delivery: {
    city: string;
    firstName: string;
    lastName: string;
    addressLine2: string;
    postalCode: string;
    address: string;
    countryCode: string;
  };
};
function savePendingCardOrder(args: SavePendingCardOrderArgs) {
  localStorage.setItem('orderCardFromSignup', JSON.stringify(args));
}

function getPendingCardOrder() {
  const order = localStorage.getItem('orderCardFromSignup');

  if (!order) {
    return undefined;
  }
  return JSON.parse(order) as SavePendingCardOrderArgs;
}

function clearPendingCardOrder() {
  localStorage.removeItem('orderCardFromSignup');
}
